import { selectCurrentHotelName } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import FolderEmpty from 'components/ReusableComponents/Icons/FolderEmpty'
import FolderFilled from 'components/ReusableComponents/Icons/FolderFilled'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark'
import subsidiariesApi from 'services/api/subsidiaries'

import { ReactComponent as DragIcon } from './drag.svg'
import { ReactComponent as NoConversionIcon } from './noConvert.svg'
import { ReactComponent as NoReachIcon } from './noReach.svg'
import styles from './RateCodes.module.scss'

async function getMergedRateCodes(isIndependent) {
  return new Promise((resolve, reject) => {
    if (isIndependent) {
      subsidiariesApi.rateCodes
        .get({ isSubsidiaryLevel: true })
        .then(res => resolve(res))
        .catch(err => reject(err))
      return
    }

    subsidiariesApi.rateCodes
      .get({ isSubsidiaryLevel: false })
      .then(brandLevelRates => {
        subsidiariesApi.rateCodes
          .get({ isSubsidiaryLevel: true })
          .then(hotelLevelRates => {
            const mergedRates = { ...hotelLevelRates }

            Object.keys(brandLevelRates).forEach(category => {
              if (category !== 'Unknown') {
                // For other categories, remove rates from Unknown in hotel level if they exist in brand level category
                const brandLevelRatesToAdd = brandLevelRates[category].map(rate => ({
                  ...rate,
                  fromBrandLevel: true
                }))

                mergedRates[category] = [
                  ...(mergedRates[category] || []),
                  ...brandLevelRatesToAdd
                ]

                // Remove these rates from Unknown in hotel level
                if (mergedRates.Unknown) {
                  mergedRates.Unknown = mergedRates.Unknown.filter(
                    rate => !brandLevelRates[category].some(r => r.code === rate.code)
                  )
                }
              }
            })
            resolve(mergedRates)
          })
          .catch(err => reject(err))
      })
      .catch(err => reject(err))
  })
}

// public enum RateKind
// {
//     OTA               = 0,
//     OTAOpaque         = 1,
//     Direct            = 10,
//     FriendsAndFamily  = 20,
//     Employee          = 21,
//     AirlineDistress   = 29,
//     CorporateLocal    = 30,
//     CorporateBrand    = 40,
//     GovernmentLocal   = 50,
//     GovernmentBrand   = 60,
//     Complimentary     = 70,
//     RewardPointsClaim = 80,
//     LongTermResident  = 90,
//     DiscountLocal     = 100,
//     DiscountBrand     = 110,
//     DiscountClub      = 120,
//     DirectfulCode     = 130,
//     Unknown           = 999,
// }

const columnNames = {
  DirectfulCode: 'Directful',
  OTA: 'OTA',
  OTAOpaque: 'Opaque rates',
  Employee: 'Employee',
  FriendsAndFamily: 'Friends & family',
  LongTermResident: 'Long term resident',
  AirlineDistress: 'Airline distress',
  Unknown: 'Uncategorized',
  CorporateLocal: 'Corporate local',
  CorporateBrand: 'Corporate brand',
  GovernmentLocal: 'Government local',
  GovernmentBrand: 'Government brand',
  Complimentary: 'Complimentary',
  RewardPointsClaim: 'Reward points claim',
  DiscountLocal: 'Discount local',
  DiscountBrand: 'Discount brand',
  DiscountClub: 'Discount club',
  Direct: 'Direct'
}

const Category = ({
  id,
  codes,
  isCollapsedByDefault,
  isCollapsable,
  isFiltered,
  isNoConvert = false,
  isNoReach = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault)
  if (id === 'Unknown' && codes?.length > 0 && isFiltered) {
    return (
      <Droppable droppableId={id}>
        {droppableProvided => (
          <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
            <Draggable draggableId="all-filtered" index={-1}>
              {(provided, snapshot) => (
                <div className={styles.category}>
                  <div
                    className={styles.categoryTitle}
                    onClick={() => (isCollapsable ? setIsCollapsed(c => !c) : null)}
                    style={{ cursor: isCollapsable ? 'pointer' : 'default' }}
                  >
                    {id === 'Unknown' ? (
                      <FolderEmpty />
                    ) : (
                      <FolderFilled
                        fill={id === 'DirectfulCode' ? 'rgb(155, 81, 224)' : '#A8ADB2'}
                      />
                    )}{' '}
                    {columnNames[id]} ({codes && codes.length})
                  </div>
                  {!isCollapsed && (
                    <>
                      <div {...provided.draggableProps}>
                        <div
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className={styles.allFilteredHandle}
                        >
                          <DragIcon
                            style={{
                              width: '16px',
                              height: '16px',
                              verticalAlign: 'middle',
                              marginRight: '8px',
                              marginBottom: '2px',
                              fill: snapshot.isDragging ? 'var(--black)' : 'var(--gray)'
                            }}
                          />
                          Drag all filtered
                        </div>
                        <div
                          className={
                            codes && codes.length > 0
                              ? styles.categoryInner
                              : styles.categoryInnerEmpty
                          }
                          style={{
                            borderColor:
                              id === 'Directful' ? 'rgba(155, 81, 224, 0.4)' : null,
                            borderWidth: id === 'Directful' ? '2px' : null
                          }}
                        >
                          {codes?.length > 0 &&
                            codes.map((item, index) => (
                              <RateCard key={item.code} item={item} index={index} />
                            ))}
                          {droppableProvided.placeholder}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Draggable>
          </div>
        )}
      </Droppable>
    )
  }

  return (
    <Droppable droppableId={id}>
      {provided => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={styles.category}
        >
          <div
            className={styles.categoryTitle}
            onClick={() => (isCollapsable ? setIsCollapsed(c => !c) : null)}
            style={{ cursor: isCollapsable ? 'pointer' : 'default' }}
          >
            {id === 'Unknown' ? (
              <FolderEmpty />
            ) : (
              <FolderFilled
                fill={id === 'DirectfulCode' ? 'rgb(155, 81, 224)' : '#A8ADB2'}
              />
            )}{' '}
            {columnNames[id]}
            {isCollapsable && (
              <button className={styles.collapseButton}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 10l-1.41-1.41L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                    fill="#A8ADB2"
                    style={{
                      transform: isCollapsed ? 'rotate(0)' : 'rotate(180deg)',
                      transformOrigin: 'center'
                    }}
                  />
                </svg>
              </button>
            )}
            {id === 'Unknown' && ` (${codes && codes.length})`}
            <div className={styles.exclusionIcons}>
              {isNoReach && <NoReachIcon />}
              {isNoConvert && <NoConversionIcon />}
            </div>
          </div>
          {!isCollapsed && (
            <>
              <div
                className={
                  codes && codes.length > 0
                    ? styles.categoryInner
                    : styles.categoryInnerEmpty
                }
                style={{
                  borderColor: id === 'Directful' ? 'rgba(155, 81, 224, 0.4)' : null,
                  borderWidth: id === 'Directful' ? '2px' : null
                }}
              >
                {id === 'Unknown' && isFiltered && codes?.length > 0 && (
                  <AllItemsCard count={codes ? codes.length : 0} />
                )}
                {codes?.length > 0 &&
                  codes.map((item, index) => (
                    <RateCard key={item.code} item={item} index={index} />
                  ))}
                {provided.placeholder}
              </div>
            </>
          )}
        </div>
      )}
    </Droppable>
  )
}

const RateCard = ({ item, index }) => (
  <Draggable
    key={item.code}
    draggableId={item.code}
    index={index}
    isDragDisabled={item.fromBrandLevel === true}
  >
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={snapshot.isDragging ? styles.rateCardDragging : styles.rateCard}
        style={{
          ...provided.draggableProps.style,
          backgroundColor: item.fromBrandLevel ? '#faf4ff' : 'white',
          cursor: item.fromBrandLevel ? 'not-allowed' : 'grab'
        }}
      >
        <span className={styles.rateCode}>{item.code}</span>

        {item.fromBrandLevel ? (
          <span
            className={styles.bookingsCount}
            style={{ fontStyle: 'italic', opacity: 0.7 }}
          >
            Inherited from the brand
          </span>
        ) : (
          <span className={styles.bookingsCount}>{item.bookingsCount} bookings</span>
        )}
      </div>
    )}
  </Draggable>
)

const AllItemsCard = ({ count }) => (
  <Draggable key="all-filtered" draggableId="all-filtered" index={-1}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={snapshot.isDragging ? styles.rateCardDragging : styles.rateCard}
      >
        <span className={styles.allRateCodes}>All filtered</span>
        <span className={styles.bookingsCount}>{count} codes</span>
      </div>
    )}
  </Draggable>
)

const RateCodes = () => {
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)
  const shouldDisplayParentChainTab = parentChainCode !== 'independent'

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [tab, setTab] = useState('hotelLevel')
  const [showUnchangesWarningDialog, setShowUnchangesWarningDialog] = useState(false)

  const [rateState, setRateState] = useState(null)

  const [unknownRates, setUnknownRates] = useState([])

  const [changes, setChanges] = useState([])

  const currentHotelName = useSelector(selectCurrentHotelName)

  const [filterValue, setFilterValue] = useState('')
  const [filterType, setFilterType] = useState('Starts with')

  useEffect(() => {
    if (rateState === null) {
      return
    }

    const unknownRates = rateState.Unknown || []

    const filteredRates = unknownRates.filter(rate => {
      if (filterType === 'Starts with') {
        return rate.code.toLowerCase().startsWith(filterValue.toLowerCase())
      }
      if (filterType === 'Contains') {
        return rate.code.toLowerCase().includes(filterValue.toLowerCase())
      }
      return true
    })

    setUnknownRates(filteredRates)
  }, [filterValue, filterType, rateState])

  const history = useHistory()

  useEffect(() => {
    setIsLoading(true)

    if (tab === 'hotelLevel') {
      getMergedRateCodes(parentChainCode === 'independent')
        .then(res => {
          setRateState(res)
          setUnknownRates(res.Unknown)
          setIsErred(false)
        })
        .catch(() => {
          setIsErred(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
      return
    }

    subsidiariesApi.rateCodes
      .get({ isSubsidiaryLevel: tab === 'hotelLevel' })
      .then(res => {
        setRateState(res)
        setUnknownRates(res.Unknown)
        setIsErred(false)
      })
      .catch(() => {
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [tab])

  function onSave() {
    if (changes.length === 0) {
      return
    }

    setIsLoading(true)

    subsidiariesApi.rateCodes
      .put({ rateCodes: changes })
      .then(() => {
        history.push('/hotelDetails')
      })
      .catch(() => {
        setIsErred(true)
      })
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const items = [...rateState[source.droppableId]]
      const [removed] = items.splice(source.index, 1)
      items.splice(destination.index, 0, removed)

      setRateState({
        ...rateState,
        [source.droppableId]: items
      })
    }

    if (source.droppableId !== destination.droppableId) {
      const allSourceItems = rateState[source.droppableId] || []
      let sourceItems = [...rateState[source.droppableId]]

      if (source.droppableId === 'Unknown') {
        sourceItems = [...unknownRates]
      }

      if (source.index === -1) {
        const updatedDestItems = [...rateState[destination.droppableId], ...unknownRates]

        const updatedSourceItems = allSourceItems.filter(
          i => unknownRates.find(u => u.code === i.code) === undefined
        )

        setChanges(c => [
          ...c.filter(prev => !unknownRates.find(r => r.code === prev.code)),
          ...unknownRates.map(r => ({
            isSubsidiaryLevel: tab === 'hotelLevel',
            code: r.code,
            kind: destination.droppableId
          }))
        ])

        setRateState({
          ...rateState,
          [source.droppableId]: updatedSourceItems,
          [destination.droppableId]: updatedDestItems
        })
        return
      }

      const targetItem = sourceItems[source.index]

      const destItems = [...rateState[destination.droppableId]]

      // remove the targetItem from allSourceItems

      const updatedSourceItems = allSourceItems.filter(i => i.code !== targetItem.code)

      setChanges(c => [
        ...c.filter(prev => prev.code !== targetItem.code),
        {
          isSubsidiaryLevel: tab === 'hotelLevel',
          code: targetItem.code,
          kind: destination.droppableId
        }
      ])

      const updatedDestItems = [...destItems, targetItem]

      setRateState({
        ...rateState,
        [source.droppableId]: updatedSourceItems,
        [destination.droppableId]: updatedDestItems
      })
    }
  }

  const handleTabChange = tab => {
    if (changes.length > 0) {
      setShowUnchangesWarningDialog(true)
    } else {
      setTab(tab)
    }
  }

  const handleUnsavedChangesSubmit = () => {
    setChanges([])
    setShowUnchangesWarningDialog(false)
    setTab(prev =>
      prev === 'hotelLevel' ? parentChainNames[parentChainCode] : 'hotelLevel'
    )
  }

  return (
    <>
      <ConfirmationDialog
        maxWidth={1000}
        title={`Rate codes categorization of ${currentHotelName}`}
        theme="blacklist"
        positiveMessage="Save changes"
        negativeMessage="Cancel"
        contentPadding="0"
        onReject={() => history.push('/hotelDetails')}
        onConfirm={onSave}
      >
        {isErred && <div className={styles.error}>Something went wrong</div>}
        {!isErred && (
          <div className={styles.wrapper}>
            {isLoading ? (
              <LoadingIndicator />
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <div className={styles.filterContainer}>
                  <div className={styles.tabs}>
                    <button
                      className={
                        tab === 'hotelLevel' ? styles.activeTab : styles.emptyTab
                      }
                      onClick={() =>
                        shouldDisplayParentChainTab ? handleTabChange('hotelLevel') : null
                      }
                    >
                      Hotel level
                    </button>
                    {shouldDisplayParentChainTab && (
                      <button
                        className={
                          tab !== 'hotelLevel' ? styles.activeTab : styles.emptyTab
                        }
                        onClick={() => handleTabChange(parentChainNames[parentChainCode])}
                      >
                        {`Brand level`}
                      </button>
                    )}
                    <div className={styles.tabIcons}>
                      <div className={styles.tabIcon}>
                        <NoReachIcon />
                        <span>Will not reached</span>
                      </div>
                      <div className={styles.tabIcon}>
                        <NoConversionIcon />
                        <span>Will not convert</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.filterContainer}>
                  <TextInput
                    placeholder="Search rate codes"
                    className={styles.search}
                    value={filterValue}
                    onChange={e => setFilterValue(e.target.value)}
                    type="search"
                  />
                  <Dropdown
                    options={{
                      'Starts with': 'Starts with',
                      Contains: 'Contains'
                    }}
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className={styles.dropdown}
                  />
                </div>
                <div className={styles.columns}>
                  <div className={styles.column}>
                    <Category id="DirectfulCode" codes={rateState.DirectfulCode} />
                    <Category id="OTA" codes={rateState.OTA} isCollapsable isNoConvert />
                    <Category
                      id="OTAOpaque"
                      codes={rateState.OTAOpaque}
                      isCollapsable
                      isNoConvert
                      isCollapsedByDefault={rateState.OTAOpaque.length > 0}
                    />
                    <Category
                      id="Employee"
                      codes={rateState.Employee}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.Employee.length > 0}
                    />
                    <Category
                      id="FriendsAndFamily"
                      codes={rateState.FriendsAndFamily}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.FriendsAndFamily.length > 0}
                    />
                    <Category
                      id="LongTermResident"
                      codes={rateState.LongTermResident}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.LongTermResident.length > 0}
                    />
                    <Category
                      id="AirlineDistress"
                      codes={rateState.AirlineDistress}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.AirlineDistress.length > 0}
                    />
                    <Category
                      id="CorporateLocal"
                      codes={rateState.CorporateLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="CorporateBrand"
                      codes={rateState.CorporateBrand}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="GovernmentLocal"
                      codes={rateState.GovernmentLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="GovernmentBrand"
                      codes={rateState.GovernmentBrand}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="Complimentary"
                      codes={rateState.Complimentary}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="RewardPointsClaim"
                      codes={rateState.RewardPointsClaim}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountLocal"
                      codes={rateState.DiscountLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountBrand"
                      codes={rateState.DiscountBrand}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountClub"
                      codes={rateState.DiscountClub}
                      isCollapsable
                      isCollapsedByDefault
                    />
                  </div>
                  <div className={styles.column}>
                    <Category
                      id="Unknown"
                      codes={unknownRates}
                      isFiltered={filterValue !== ''}
                    />
                  </div>
                </div>
              </DragDropContext>
            )}
          </div>
        )}
      </ConfirmationDialog>
      {showUnchangesWarningDialog && (
        <ConfirmationDialog
          title="Unsaved changes"
          positiveMessage="Discard"
          negativeMessage="Go back"
          theme="blacklist"
          onConfirm={handleUnsavedChangesSubmit}
          onClose={() => setShowUnchangesWarningDialog(false)}
          onPressEnter={handleUnsavedChangesSubmit}
        >
          <div>You've unsaved changes. Are you sure you want to leave?</div>
        </ConfirmationDialog>
      )}
    </>
  )
}

export default RateCodes
